import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import i18n from "../../../i18n";
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
/**
 * Parses a date string or Date object to Dayjs instance.
 */
export const parseDate = (input) => {
    return dayjs(input === null || input === void 0 ? void 0 : input.toString());
};
/**
 * Formats a Dayjs or date input to "MMMM Do, YYYY" (e.g., January 20th, 2025)
 */
export const formatToReadableDate = (input) => {
    return parseDate(input).format("MMMM Do, YYYY");
};
/**
 * Converts "HH:mm:ss" or "HH:mm" to "HH:mm"
 */
export const formatToHHmm = (time) => {
    if (time && time != "") {
        const [hours, minutes] = time.split(":");
        return `${hours}:${minutes}`;
    }
    return "";
};
/**
 * Formats time range: "09:30 - 12:00"
 */
export const formatTimeRange = (start, end) => {
    return `${formatToHHmm(start)} - ${formatToHHmm(end)}`;
};
export const normalizeDateToLocalDateOnly = (date) => {
    if (!date)
        return null;
    const jsDate = typeof date === "string" ? new Date(date) : date;
    // This returns a new Date at local midnight (00:00:00) without timezone shifts
    return new Date(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate());
};
/**
 * Converts a UTC date string or Date object to a local Date object.
 * Returns null if input is invalid.
 */
export const utcToLocalDateTime = (date) => {
    if (!date)
        return null;
    const utcDate = typeof date === "string"
        ? new Date(date.endsWith("Z") ? date : date + "Z")
        : date;
    if (isNaN(utcDate.getTime()))
        return null;
    // Returns a Date object in local time zone with the same timestamp as the UTC input
    return new Date(utcDate.getTime());
};
export const combineLocalDateAndTime = (date, time) => {
    const combined = new Date(date);
    combined.setHours(time.getHours());
    combined.setMinutes(time.getMinutes());
    combined.setSeconds(time.getSeconds() || 0);
    combined.setMilliseconds(time.getMilliseconds() || 0);
    return combined;
};
/**
 * Converts a UTC date string to a local Date object.
 * Appends "Z" if the string does not already end with "Z" to ensure
 * the browser treats it as UTC before converting to local.
 */
const ensureUtcDate = (input) => {
    if (!input)
        return null;
    const date = typeof input === "string"
        ? new Date(input.endsWith("Z") ? input : input + "Z")
        : input;
    return isNaN(date.getTime()) ? null : date;
};
/**
 * Formats a UTC datetime to local date string: "16 Feb. 2026"
 */
export const formatUtcToLocalDate = (input) => {
    const date = ensureUtcDate(input);
    if (!date)
        return "";
    if (date.getFullYear() <= 1)
        return "N/A";
    const locale = i18n.language || undefined;
    const day = date.getDate();
    const month = date.toLocaleDateString(locale, { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month}. ${year}`;
};
/**
 * Formats a UTC datetime to local time string: "14:30:00"
 */
export const formatUtcToLocalTime = (input, showSeconds = true) => {
    const date = ensureUtcDate(input);
    if (!date)
        return "";
    if (date.getFullYear() <= 1)
        return "";
    const locale = i18n.language || undefined;
    const options = Object.assign({ hour12: false, hour: "2-digit", minute: "2-digit" }, (showSeconds ? { second: "2-digit" } : {}));
    return date.toLocaleTimeString(locale, options);
};
/**
 * Formats a UTC datetime to a full local datetime string: "16 Feb 2026, 14:30:00"
 */
export const formatUtcToLocalDateTimeString = (input, showSeconds = true) => {
    const date = ensureUtcDate(input);
    if (!date)
        return "";
    const locale = i18n.language || undefined;
    return date.toLocaleString(locale, Object.assign({ day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }, (showSeconds ? { second: "2-digit" } : {})));
};
/**
 * Formats a UTC datetime to a verbose local datetime string:
 * e.g. "Monday, 16 February 2026, 14:30:00"
 */
export const formatUtcToLocalDateTimeVerbose = (input) => {
    const date = ensureUtcDate(input);
    if (!date)
        return "";
    const locale = i18n.language || undefined;
    return date.toLocaleString(locale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });
};
/**
 * Formats a UTC datetime to a short local date string: "16/02/2026"
 */
export const formatUtcToLocalShortDate = (input) => {
    const date = ensureUtcDate(input);
    if (!date)
        return "";
    const locale = i18n.language || undefined;
    return date.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};
